"use client";

import { useRef, useState, useEffect, type ReactNode, type RefObject } from "react";
import Link from "next/link";

/* ─── Scroll reveal ───────────────────────────────────────── */
function useVisible<T extends HTMLElement = HTMLDivElement>(threshold = 0.1): [RefObject<T>, boolean] {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, from = "bottom" }: { children: ReactNode; delay?: number; from?: "bottom" | "left" | "right" }) {
  const [ref, v] = useVisible();
  const hidden: Record<"bottom" | "left" | "right", string> = {
    bottom: "opacity-0 translate-y-7",
    left:   "opacity-0 -translate-x-7",
    right:  "opacity-0 translate-x-7",
  };
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${v ? "opacity-100 translate-x-0 translate-y-0" : hidden[from]}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

/* ─── Shared UI ───────────────────────────────────────────── */
function Eyebrow({ text, light = false }: { text: string; light?: boolean }) {
  return (
    <span className={`block text-[11px] font-bold tracking-[0.15em] uppercase mb-3.5 ${light ? "text-blue-300" : "text-blue-700"}`}>
      {text}
    </span>
  );
}
function Divider({ light = false }) {
  return <div className={`w-11 h-[3px] mb-6 ${light ? "bg-blue-400" : "bg-blue-700"}`} />;
}

/* ─── Data ────────────────────────────────────────────────── */
const DEVOPS_STATS = [
  { v: "10x",    l: "Faster deployment cycles",       src: "DORA Report" },
  { v: "60%",    l: "Reduction in change failure rate", src: "DORA 2023" },
  { v: "24x",    l: "More frequent deployments",       src: "State of DevOps" },
  { v: "2,604x", l: "Faster incident recovery (elite)", src: "DORA 2023" },
];

const SERVICES = [
  {
    n: "01", title: "CI/CD Pipeline Design & Implementation",
    body: "Design and build automated continuous integration and continuous deployment pipelines using GitHub Actions, Jenkins, GitLab CI, or CircleCI — enabling multiple deployments per day with zero manual intervention.",
    img: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=600&q=80",
    tags: ["GitHub Actions", "Jenkins", "GitLab CI", "CircleCI"],
  },
  {
    n: "02", title: "Infrastructure as Code (IaC)",
    body: "Define, version and automate your entire infrastructure using Terraform, Pulumi, or AWS CloudFormation. Repeatable, auditable and environment-consistent infrastructure at any scale.",
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&q=80",
    tags: ["Terraform", "Pulumi", "CloudFormation"],
  },
  {
    n: "03", title: "Containerization & Orchestration",
    body: "Containerize your applications with Docker and orchestrate at scale with Kubernetes (EKS, AKS, GKE). Service mesh, auto-healing, auto-scaling and blue-green deployments.",
    img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&q=80",
    tags: ["Docker", "Kubernetes", "EKS / AKS / GKE"],
  },
  {
    n: "04", title: "Monitoring & Observability",
    body: "End-to-end visibility into application performance, infrastructure health and user experience. Prometheus, Grafana, Datadog, ELK Stack and PagerDuty integration for proactive alerting.",
    img: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&q=80",
    tags: ["Prometheus", "Grafana", "Datadog", "ELK Stack"],
  },
  {
    n: "05", title: "DevSecOps (Security in Pipeline)",
    body: "Shift security left — integrate SAST, DAST, container scanning, dependency checking and secrets management directly into your CI/CD pipeline. Security without slowing delivery.",
    img: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&q=80",
    tags: ["SAST / DAST", "Container scanning", "Secrets mgmt"],
  },
  {
    n: "06", title: "Site Reliability Engineering (SRE)",
    body: "Reliability-focused engineering — SLO/SLI definition, error budget management, runbook creation, incident management and chaos engineering to build systems that stay up.",
    img: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=600&q=80",
    tags: ["SLO / SLI", "Error budgets", "Chaos engineering"],
  },
];

const TOOLS = [
  { category: "Version Control",  tools: "Git, GitHub, GitLab, Bitbucket" },
  { category: "CI/CD",            tools: "GitHub Actions, Jenkins, GitLab CI, CircleCI, ArgoCD" },
  { category: "Containers",       tools: "Docker, Podman, containerd" },
  { category: "Orchestration",    tools: "Kubernetes, Helm, Kustomize" },
  { category: "IaC",              tools: "Terraform, Pulumi, Ansible, AWS CloudFormation" },
  { category: "Monitoring",       tools: "Prometheus, Grafana, Datadog, New Relic, ELK Stack" },
  { category: "Security",         tools: "Snyk, Trivy, HashiCorp Vault, OWASP ZAP" },
  { category: "Cloud",            tools: "AWS, Azure, GCP, DigitalOcean" },
];

const STEPS = [
  { n: "01", title: "Assessment",            body: "Audit current SDLC, deployment process and infrastructure maturity to establish a baseline." },
  { n: "02", title: "Roadmap",               body: "Define a phased DevOps adoption roadmap with clear milestones and measurable KPIs." },
  { n: "03", title: "Foundation",            body: "Set up version control, branching strategy and basic CI pipelines across teams." },
  { n: "04", title: "Automation",            body: "Build full CI/CD pipelines, automated testing suites and deployment workflows end-to-end." },
  { n: "05", title: "Infrastructure",        body: "Implement IaC, containerization and cloud-native architecture for scalable, repeatable environments." },
  { n: "06", title: "Observe & Secure",      body: "Deploy monitoring, proactive alerting and DevSecOps integration across the full pipeline." },
  { n: "07", title: "Continuous Improvement",body: "Review metrics, optimise MTTR/MTBF and scale DevOps practices across all engineering teams." },
];

const FAQS = [
  { q: "What is DevOps and why does it matter for our business?",        a: "DevOps combines development and operations into a unified practice — automating deployment, improving software quality and enabling faster, more reliable releases. Businesses that adopt DevOps deploy 24x more frequently and recover from incidents 2,600x faster than those that don&apos;t." },
  { q: "What is CI/CD and how does it improve our release process?",     a: "CI (Continuous Integration) automatically tests every code change as it&apos;s committed. CD (Continuous Deployment) then automatically releases validated code to production. Together they eliminate manual deployment bottlenecks and dramatically reduce the risk of each release." },
  { q: "Why containerise with Docker and orchestrate with Kubernetes?",  a: "Docker packages your application and all its dependencies into a portable container — eliminating &apos;it works on my machine&apos; problems. Kubernetes then manages those containers at scale: automatic healing, load balancing, rolling updates and efficient resource utilisation across your infrastructure." },
  { q: "What is Infrastructure as Code and why does it matter?",         a: "IaC means defining your entire infrastructure — servers, networks, databases — in version-controlled code files. This makes environments fully reproducible, eliminates configuration drift between dev/staging/production and allows your infrastructure to be reviewed, tested and rolled back like application code." },
  { q: "What is DevSecOps and how is it different from traditional security?", a: "DevSecOps integrates security checks directly into the CI/CD pipeline rather than treating security as a final gate before release. Automated SAST, DAST, container scanning and secrets detection run on every commit — finding vulnerabilities immediately when they're cheapest to fix, not weeks later at release time." },
  { q: "How long does a full DevOps transformation take?",               a: "Most teams see meaningful quick wins within 2–3 months: faster deployments, reduced manual work and basic CI pipelines. A full transformation — including cultural change, IaC adoption and mature observability — typically takes 6–12 months depending on team size and existing infrastructure complexity." },
];

/* ─── FAQ Item ────────────────────────────────────────────── */
function FaqItem({ q, a, i }: { q: string; a: string; i: number }) {
  const [open, setOpen] = useState(false);
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
      style={{ transitionDelay: `${i * 0.06}s` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left flex justify-between items-center gap-4 py-[22px] bg-transparent border-none cursor-pointer"
      >
        <span className="text-[15px] font-semibold text-slate-900 leading-snug">{q}</span>
        <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center transition-colors duration-200 ${open ? "bg-blue-700" : "bg-slate-100"}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d={open ? "M2 6h8" : "M6 2v8M2 6h8"} stroke={open ? "#fff" : "#1565C0"} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </div>
      </button>
      {open && (
        <div className="pb-[22px]">
          <p className="text-[14px] text-slate-500 leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
}

/* ─── Service Card ────────────────────────────────────────── */
function ServiceCard({ s, i }: { s: { tags: string[]; img: string; title: string; body: string; n: string }; i: number }) {
  const [ref, v] = useVisible();
  const [hov, setHov] = useState(false);
  return (
    <div
      ref={ref}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className={`flex flex-col overflow-hidden bg-white border transition-all duration-500 ${hov ? "border-slate-300 shadow-xl" : "border-slate-200 shadow-sm"}`}
      style={{ transitionDelay: `${(i % 3) * 0.09}s`, opacity: v ? 1 : 0, transform: v ? "none" : "translateY(24px)" }}
    >
      <div className="relative h-[160px] overflow-hidden">
        <img
          src={s.img} alt={s.title}
          className={`w-full h-full object-cover transition-transform duration-500 ${hov ? "scale-105" : "scale-100"}`}
        />
        <div className="absolute top-3.5 left-3.5 bg-blue-700 text-white text-[11px] font-bold tracking-[0.08em] px-2.5 py-1">
          {s.n}
        </div>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-[13.5px] font-bold text-slate-900 mb-2 leading-snug">{s.title}</p>
        <p className="text-[13px] text-slate-500 leading-relaxed mb-3.5">{s.body}</p>
        <div className="flex flex-wrap gap-1.5 mt-auto">
          {s.tags.map(tag => (
            <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 tracking-wide">{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Step ────────────────────────────────────────────────── */
function Step({ s, i, last }: { s: { n: string; title: string; body: string }; i: number; last?: boolean }) {
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`flex gap-4 items-start relative transition-all duration-500 ${!last ? "pb-7" : ""}`}
      style={{ transitionDelay: `${(i % 4) * 0.08}s`, opacity: v ? 1 : 0, transform: v ? "none" : "translateY(20px)" }}
    >
      {!last && <div className="absolute left-[17px] top-9 w-px bottom-0 bg-slate-200" />}
      <div className="w-9 h-9 bg-blue-700 flex-shrink-0 flex items-center justify-center">
        <span className="text-[11px] font-bold text-white">{s.n}</span>
      </div>
      <div className="pt-1.5">
        <p className="text-[14px] font-bold text-slate-900 mb-1.5">{s.title}</p>
        <p className="text-[13.5px] text-slate-500 leading-relaxed">{s.body}</p>
      </div>
    </div>
  );
}

/* ─── Tools Table Row ─────────────────────────────────────── */
function ToolRow(props: { row: { category: string; tools: string }; i: number }) {
  const { row, i } = props;
  const [ref, v] = useVisible();
  return (
    <div
      ref={ref}
      className={`grid grid-cols-[220px_1fr] border-b border-slate-200 transition-all duration-500 ${v ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"} ${i === 0 ? "border-t" : ""}`}
      style={{ transitionDelay: `${i * 0.05}s` }}
    >
      <div className="py-4 px-5 bg-slate-50 border-r border-slate-200">
        <span className="text-[12px] font-bold text-slate-700 tracking-[0.06em] uppercase">{row.category}</span>
      </div>
      <div className="py-4 px-5">
        <span className="text-[13.5px] text-slate-500 leading-relaxed">{row.tools}</span>
      </div>
    </div>
  );
}

/* ─── Page ────────────────────────────────────────────────── */
export default function DevOpsPage() {
  return (
    <main className="bg-white">

      {/* ══ HERO ═══════════════════════════════════════════ */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-0 lg:min-h-[580px]">
        <div className="flex flex-col justify-center px-4 sm:px-8 lg:px-20 py-[30px] bg-white">
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6">
            <Link href="/services" className="text-blue-700 font-medium hover:underline">Services</Link>
            <span className="text-slate-300">›</span>
            <Link href="/services/it" className="text-blue-700 font-medium hover:underline">IT Solutions</Link>
            <span className="text-slate-300">›</span>
            <span>DevOps</span>
          </nav>

          <div className="flex flex-wrap gap-2 mb-5">
            {["CI/CD", "Kubernetes", "Terraform", "DevSecOps", "SRE", "IaC"].map(tag => (
              <span key={tag} className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 tracking-wide">{tag}</span>
            ))}
          </div>

          <Eyebrow text="IT Solutions — DevOps" />
          <Divider />

          <h1 className="text-[clamp(26px,3.5vw,46px)] font-extrabold text-slate-900 leading-[1.1] mb-5 ">
            Ship Faster. Break Nothing. Scale with Confidence.
          </h1>
          <p className="text-[16px] font-light text-slate-500 leading-relaxed mb-9 ">
            Modern DevOps practices that automate your delivery pipeline, ensure infrastructure reliability and reduce time-to-market from weeks to hours. Build, test, deploy — continuously.
          </p>
          <div className="flex gap-3.5">
            <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-7 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
              BOOK DEVOPS CONSULTATION
            </button>
            <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-7 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
              FREE ASSESSMENT
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden">
          <img
            src="/images/services/IT/Banner/devops.png"
            alt="DevOps automation"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Blue stat strip */}
      <div className="bg-blue-700 grid grid-cols-2 lg:grid-cols-4">
        {[
          { v: "24x",    l: "More Frequent Deployments" },
          { v: "6",      l: "Core DevOps Services" },
          { v: "60%",    l: "Lower Change Failure Rate" },
          { v: "Free",   l: "Initial Assessment" },
        ].map((s, i) => (
          <div key={i} className={`py-5 text-center ${i < 3 ? "border-r border-white/15" : ""}`}>
            <p className="text-[22px] font-black text-white leading-none">{s.v}</p>
            <p className="text-[11px] font-medium text-white/65 mt-1.5 tracking-widest uppercase">{s.l}</p>
          </div>
        ))}
      </div>

      {/* ══ WHY DEVOPS ═════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-900">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <Eyebrow text="Why DevOps Matters" light />
            <Divider light />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-white leading-tight mb-5">
              The Gap Between Fast Teams and the Rest
            </h2>
            <p className="text-[15px] font-light text-slate-300 leading-relaxed mb-8">
              Elite engineering teams deploy on demand — multiple times a day — while traditional teams ship every few weeks. The difference isn&apos;t talent. It&apos;s process, tooling and culture. DevOps closes that gap systematically.
            </p>
            <div className="flex flex-col gap-4">
              {[
                "Elite DevOps teams deploy 24x more frequently than low performers (DORA 2023)",
                "Change failure rates drop by 60% after mature CI/CD pipeline adoption",
                "Incident recovery time improves by 2,604x for high-performing teams",
                "Companies with strong DevOps practices grow revenue 2x faster (Puppet State of DevOps)",
              ].map((stat, i) => (
                <div key={i} className="flex items-start gap-3.5">
                  <div className="w-5 h-5 bg-blue-600 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M1.5 5.5L3.8 7.8L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-[14px] text-slate-300 leading-relaxed">{stat}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal from="right" delay={0.12}>
            <div className="grid grid-cols-2 gap-0.5">
              {DEVOPS_STATS.map((s, i) => (
                <div key={i} className="bg-slate-800 border border-slate-700 p-6">
                  <p className="text-[32px] font-black text-blue-400 leading-none mb-2">{s.v}</p>
                  <p className="text-[13px] text-slate-300 leading-snug mb-2">{s.l}</p>
                  <p className="text-[11px] font-semibold text-slate-500 tracking-wide uppercase">{s.src}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ SERVICES — 3-col grid ══════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto">
          <Reveal>
            <div className="mb-10 lg:mb-14">
              <Eyebrow text="DevOps Services" />
              <Divider />
              <div className="flex justify-between items-end flex-wrap gap-4">
                <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight m-0">
                  Six DevOps Capabilities.<br />One Delivery Pipeline.
                </h2>
                <p className="text-[14px] text-slate-500 max-w-[360px] leading-relaxed">
                  From pipeline design to security integration and full site reliability — end-to-end DevOps coverage.
                </p>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => <ServiceCard key={i} s={s} i={i} />)}
          </div>
        </div>
      </section>

      {/* ══ TOOLS TABLE ════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Tools & Technologies" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              DevOps Tools We Work With
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-10">
              We are tool-agnostic — we work with the best-fit solution for your stack, team and cloud environment. No vendor lock-in, no forced migrations.
            </p>
            <img
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=700&q=80"
              alt="DevOps tools and infrastructure"
              className="w-full h-[260px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="mt-1">
              {TOOLS.map((row, i) => <ToolRow key={i} row={row} i={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ 7-STEP PROCESS ═════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Our DevOps Process" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              7-Step DevOps Transformation Process
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-10">
              A structured, phased approach to DevOps adoption — from auditing your current state through to continuous improvement at scale. No big-bang migrations, no disruption to ongoing delivery.
            </p>
            <img
              src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=700&q=80"
              alt="DevOps transformation process"
              className="w-full h-[260px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="grid grid-cols-2 gap-x-10 gap-y-0">
              <div>
                {STEPS.slice(0, 4).map((s, i) => (
                  <Step key={i} s={s} i={i} last={i === 3} />
                ))}
              </div>
              <div>
                {STEPS.slice(4).map((s, i) => (
                  <Step key={i} s={s} i={i} last={i === 2} />
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ FAQ ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-white">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-start">

          <Reveal from="left">
            <Eyebrow text="Frequently Asked Questions" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,36px)] font-bold text-slate-900 leading-tight mb-6">
              Common Questions About DevOps
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Plain answers to the DevOps and CI/CD questions we hear most from engineering and product teams.
            </p>
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=700&q=80"
              alt="DevOps team collaboration"
              className="w-full h-[180px] sm:h-[240px] lg:h-[280px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <div className="border-t border-slate-200">
              {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} i={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ CTA ════════════════════════════════════════════ */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-8 lg:px-20 bg-slate-50">
        <div className="max-w-[1160px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">

          <Reveal from="left">
            <video
              src="/videos/CTA.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-[220px] sm:h-[300px] lg:h-[380px] object-cover"
            />
          </Reveal>

          <Reveal from="right" delay={0.1}>
            <Eyebrow text="Ready to Adopt DevOps?" />
            <Divider />
            <h2 className="text-[clamp(24px,3vw,38px)] font-bold text-slate-900 leading-tight mb-5">
              Book a Free DevOps Consultation
            </h2>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-3">
              We assess your current SDLC maturity, identify the highest-impact improvements and build a phased adoption plan — at no cost, no commitment required.
            </p>
            <p className="text-[15px] text-slate-500 leading-relaxed mb-9">
              Most teams discover 3–5 critical pipeline bottlenecks in their first assessment. The question is whether you find them before they cost you a release.
            </p>
            <div className="flex gap-3.5 flex-wrap">
              <button className="bg-blue-700 text-white text-[13px] font-bold tracking-[0.06em] uppercase px-8 py-3.5 hover:bg-blue-800 transition-colors border-0 cursor-pointer">
                BOOK DEVOPS CONSULTATION
              </button>
              <button className="bg-transparent text-slate-900 text-[13px] font-semibold tracking-[0.06em] uppercase px-8 py-3.5 border border-slate-200 hover:border-slate-400 transition-colors cursor-pointer">
                FREE ASSESSMENT
              </button>
            </div>
          </Reveal>
        </div>
      </section>

    </main>
  );
}